import { useState } from "react";
import Button from "components/ui/Button";
import { Checkbox } from "components/ui/Checkbox";

const AccountSettingsSection = ({ user }) => {
    const [settings, setSettings] = useState({
        emailNotifications: true,
        smsNotifications: false,
        marketingEmails: false,
        twoFactorAuth: false,
        sessionTimeout: "30",
        theme: "system",
    });

    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [confirmDelete, setConfirmDelete] = useState(false);

    const handleToggle = (key) => {
        setSettings(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handleSelectChange = (key, value) => {
        setSettings(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleSaveSettings = async () => {
        setLoading(true);
        try {
            // Simulate API call - Replace with actual service
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            setSuccessMessage("Settings saved successfully!");
            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) {
            console.error("Failed to save settings:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadData = async () => {
        try {
            // Simulate API call to get user data
            const userData = {
                user: user,
                settings: settings,
                exportedAt: new Date().toISOString()
            };

            const dataStr = JSON.stringify(userData, null, 2);
            const dataBlob = new Blob([dataStr], { type: "application/json" });
            const url = URL.createObjectURL(dataBlob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `user-data-${new Date().getTime()}.json`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Failed to download data:", error);
        }
    };

    const handleDeleteAccount = async () => {
        if (!confirmDelete) {
            setConfirmDelete(true);
            return;
        }

        setLoading(true);
        try {
            // Simulate API call - Replace with actual service
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Redirect to home or logout
            window.location.href = "/";
        } catch (error) {
            console.error("Failed to delete account:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Notification Settings */}
            <div className="bg-card border border-border rounded-lg p-6">
                <div className="mb-6">
                    <h2 className="text-xl font-semibold text-foreground">Notification Preferences</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        Manage how you receive notifications and communications
                    </p>
                </div>

                <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                        <div>
                            <p className="font-medium text-foreground">Email Notifications</p>
                            <p className="text-sm text-muted-foreground">
                                Receive booking and account notifications via email
                            </p>
                        </div>
                        <Checkbox
                            checked={settings.emailNotifications}
                            onChange={() => handleToggle("emailNotifications")}
                        />
                    </div>

                    <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                        <div>
                            <p className="font-medium text-foreground">SMS Notifications</p>
                            <p className="text-sm text-muted-foreground">
                                Receive important notifications via SMS (charges may apply)
                            </p>
                        </div>
                        <Checkbox
                            checked={settings.smsNotifications}
                            onChange={() => handleToggle("smsNotifications")}
                        />
                    </div>

                    <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                        <div>
                            <p className="font-medium text-foreground">Marketing Emails</p>
                            <p className="text-sm text-muted-foreground">
                                Receive promotional offers and updates
                            </p>
                        </div>
                        <Checkbox
                            checked={settings.marketingEmails}
                            onChange={() => handleToggle("marketingEmails")}
                        />
                    </div>
                </div>
            </div>

            {/* Security Settings */}
            <div className="bg-card border border-border rounded-lg p-6">
                <div className="mb-6">
                    <h2 className="text-xl font-semibold text-foreground">Security Settings</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        Enhance your account security
                    </p>
                </div>

                <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                        <div>
                            <p className="font-medium text-foreground">Two-Factor Authentication</p>
                            <p className="text-sm text-muted-foreground">
                                Add an extra layer of security to your account
                            </p>
                        </div>
                        <Checkbox
                            checked={settings.twoFactorAuth}
                            onChange={() => handleToggle("twoFactorAuth")}
                        />
                    </div>

                    <div className="p-3 border border-border rounded-lg">
                        <label className="font-medium text-foreground block mb-2">
                            Session Timeout
                        </label>
                        <select
                            value={settings.sessionTimeout}
                            onChange={(e) => handleSelectChange("sessionTimeout", e.target.value)}
                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                        >
                            <option value="15">15 minutes</option>
                            <option value="30">30 minutes</option>
                            <option value="60">1 hour</option>
                            <option value="240">4 hours</option>
                            <option value="480">8 hours</option>
                        </select>
                        <p className="text-xs text-muted-foreground mt-2">
                            Automatically log out after this period of inactivity
                        </p>
                    </div>

                    <div className="p-3 border border-border rounded-lg">
                        <label className="font-medium text-foreground block mb-2">
                            Theme Preference
                        </label>
                        <select
                            value={settings.theme}
                            onChange={(e) => handleSelectChange("theme", e.target.value)}
                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                        >
                            <option value="light">Light</option>
                            <option value="dark">Dark</option>
                            <option value="system">System</option>
                        </select>
                        <p className="text-xs text-muted-foreground mt-2">
                            Choose how the interface appears
                        </p>
                    </div>
                </div>

                <div className="pt-4">
                    <Button
                        onClick={handleSaveSettings}
                        disabled={loading}
                        className="px-6"
                    >
                        {loading ? "Saving..." : "Save Settings"}
                    </Button>
                </div>
            </div>

            {/* Data Management */}
            <div className="bg-card border border-border rounded-lg p-6">
                <div className="mb-6">
                    <h2 className="text-xl font-semibold text-foreground">Data & Privacy</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        Manage your personal data
                    </p>
                </div>

                <div className="space-y-3">
                    <Button
                        onClick={handleDownloadData}
                        variant="outline"
                        className="w-full"
                    >
                        📥 Download My Data
                    </Button>
                    <p className="text-xs text-muted-foreground">
                        Download a copy of your personal data in JSON format for backup or transfer purposes
                    </p>
                </div>
            </div>

            {/* Danger Zone */}
            <div className="bg-destructive/5 border border-destructive rounded-lg p-6">
                <div className="mb-6">
                    <h2 className="text-xl font-semibold text-destructive">Danger Zone</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        Irreversible actions - proceed with caution
                    </p>
                </div>

                {confirmDelete && (
                    <div className="mb-4 p-4 bg-destructive/10 border border-destructive rounded-md">
                        <p className="font-semibold text-destructive mb-3">
                            ⚠️ Are you sure you want to delete your account?
                        </p>
                        <p className="text-sm text-muted-foreground mb-4">
                            This action cannot be undone. All your data including bookings, settings, and account information will be permanently deleted.
                        </p>
                        <div className="flex gap-3">
                            <Button
                                variant="destructive"
                                onClick={handleDeleteAccount}
                                disabled={loading}
                            >
                                {loading ? "Deleting..." : "Yes, Delete My Account"}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => setConfirmDelete(false)}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                )}

                {!confirmDelete && (
                    <Button
                        variant="destructive"
                        onClick={handleDeleteAccount}
                        disabled={loading}
                    >
                        🗑️ Delete Account
                    </Button>
                )}
            </div>

            {successMessage && (
                <div className="p-4 bg-success/10 border border-success text-success rounded-md">
                    ✓ {successMessage}
                </div>
            )}
        </div>
    );
};

export default AccountSettingsSection;
